import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import {
  Color,
  DataType,
} from '@/core/interfaces/common';
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
} from '@/core/components/Icons';
import { FormattedDate } from '@/core/components/FormattedDate';
import { HoverImageWrapper } from '@/core/components/HoverImageWrapper';
import { searchMessages } from '@/core/messages/search.messages';
import { Chip } from '@/core/components/Chip';
import { ThemeDirection } from '@/core/constants/constants';
import { IconButton } from '@/core/components/IconButton';
import { ButtonVariant } from '@/core/components/Button';

import { MediaLink as MediaLinkType } from '@/features/Media/media.types';

import { getBackendImage } from '@/utils/helpers';

import {
  Caption,
  ChipWrapper,
  Content,
  IconWrapper,
  ResourceImage,
  Subtitle,
  Title,
  Wrapper,
} from '../ResourceLink.styles';

type MediaLinkProps = {
  data: DataType<MediaLinkType>;
  isSearchResult: boolean;
}

export const MediaLink = ({
  data,
  isSearchResult,
}: MediaLinkProps) => {
  const theme = useTheme();

  const {
    attributes: {
      image,
      title,
      subtitle,
      date,
      link,
    },
  } = data;

  const iconContent = useMemo(() => (
    <IconWrapper>
      <IconButton
        icon={theme.direction === ThemeDirection.LTR ? <ArrowRight /> : <ArrowLeft />}
        variant={ButtonVariant.SECONDARY}
        ariaLabel="go to link"
      />
    </IconWrapper>
  ), [theme.direction]);

  return (
    <Wrapper>
      <HoverImageWrapper>
        <ResourceImage
          src={getBackendImage(image?.data?.attributes?.url, Color.DARK_GREY)}
          alt={image?.data?.attributes?.alternativeText || title}
        />
      </HoverImageWrapper>
      <Content>
        <Title
          as="a"
          href={link as string}
          target="_blank"
          rel="noreferrer"
        >
          {title}
        </Title>
        <Subtitle>{subtitle}</Subtitle>
        {!isSearchResult && (
          <Caption>
            <FormattedDate date={date} />
          </Caption>
        )}
        {isSearchResult && (
          <ChipWrapper>
            <Chip color={Color.GREEN}>
              <ExternalLink size={16} />
              <FormattedMessage {...searchMessages.media} />
            </Chip>
          </ChipWrapper>
        )}
      </Content>
      <a
        href={link as string}
        target="_blank"
        rel="noreferrer"
        title={`link to ${title}`}
      >
        {iconContent}
      </a>
    </Wrapper>
  );
};
